import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../components/ThemeProvider";
import api from "../services/api";

/** Cabeceras ordenables (asc → desc → quitar). Mismo ciclo que contactos interesados. */
const HEADER_COLS = [
  { label: "Ref.", key: "referencia" },
  { label: "Dirección", key: null },
  { label: "Población", key: null },
  { label: "Nº llave", key: "num_llave" },
  { label: "Estado", key: null },
  { label: "Con quién", key: null },
  { label: "Desde", key: null },
  { label: "Teléfono", key: null },
  { label: "", key: null },
];

function sortValueForKey(row, key) {
  if (key === "num_llave") {
    const raw = String(row?.num_llave ?? "").trim();
    if (!raw) return null;
    // Comparación numérica real (21===21) para que Ref pueda desempatar.
    const n = Number(raw.replace(",", "."));
    if (Number.isFinite(n)) return n;
    return raw.toLowerCase();
  }
  if (key === "referencia") {
    return String(row?.referencia || "").trim().toLowerCase() || null;
  }
  return null;
}

function compareOne(a, b, key, dir) {
  const va = sortValueForKey(a, key);
  const vb = sortValueForKey(b, key);
  const aEmpty = va == null || va === "";
  const bEmpty = vb == null || vb === "";
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;
  let cmp = 0;
  if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
  else cmp = String(va).localeCompare(String(vb), "es", { sensitivity: "base", numeric: true });
  if (cmp === 0) return 0;
  return dir === "desc" ? -cmp : cmp;
}

function compareBySortStack(a, b, sortStack) {
  for (const { key, dir } of sortStack) {
    const cmp = compareOne(a, b, key, dir);
    if (cmp !== 0) return cmp;
  }
  // Estable: id como último desempate (evita que el sort del motor reordene “iguales”).
  return String(a?.id || "").localeCompare(String(b?.id || ""));
}

/**
 * Ciclo asc → desc → quitar. Acumulables en orden de clic (igual interesados):
 * el PRIMERO clicado es el criterio principal; el siguiente, desempate; etc.
 */
function cycleSortStack(prev, key) {
  const idx = prev.findIndex((s) => s.key === key);
  if (idx < 0) return [...prev, { key, dir: "asc" }];
  const cur = prev[idx];
  if (cur.dir === "asc") {
    const next = prev.slice();
    next[idx] = { key, dir: "desc" };
    return next;
  }
  return prev.filter((_, i) => i !== idx);
}

/** Ordena con pila (1.º = principal). Stable successive sorts = mismo resultado. */
function sortRowsByStack(rows, sortStack) {
  if (!sortStack.length) return rows.slice();
  const out = rows.slice();
  out.sort((a, b) => compareBySortStack(a, b, sortStack));
  return out;
}

const SORT_LABEL = { referencia: "Ref.", num_llave: "Nº llave" };

function fmtWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function etiquetaUbicacion(ubic) {
  const u = String(ubic || "oficina").toLowerCase();
  if (u === "pendiente_aceptacion") {
    return { label: "Pendiente", bg: "rgba(245,158,11,0.15)", color: "#facc15" };
  }
  if (u === "externo") {
    return { label: "Fuera (externo)", bg: "rgba(239,68,68,0.12)", color: "#4ade80" };
  }
  return { label: "En oficina", bg: "rgba(34,197,94,0.12)", color: "#4ade80" };
}

function escHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function imprimirListadoLlaves(rows) {
  const ahora = new Date().toLocaleString("es-ES", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  const fuera = rows.filter((r) =>
    ["pendiente_aceptacion", "externo"].includes(String(r.llaves_ubicacion || "")),
  ).length;
  const bodyRows = rows
    .map((r) => {
      const est = etiquetaUbicacion(r.llaves_ubicacion).label;
      const quien = r.llaves_con_quien || [r.agencia, r.persona].filter(Boolean).join(" · ") || "—";
      return `<tr>
        <td>${escHtml(r.referencia || "—")}</td>
        <td>${escHtml(r.direccion || "—")}</td>
        <td>${escHtml(r.poblacion || "—")}</td>
        <td>${escHtml(r.num_llave || "—")}</td>
        <td>${escHtml(est)}</td>
        <td>${escHtml(quien)}</td>
        <td>${escHtml(fmtWhen(r.llaves_desde))}</td>
        <td>${escHtml(r.llaves_telefono || "—")}</td>
      </tr>`;
    })
    .join("");

  const html = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="utf-8" />
  <title>Listado de llaves — Vitalcasa</title>
  <style>
    @page { size: A4 landscape; margin: 12mm; }
    body { font-family: 'Segoe UI', system-ui, sans-serif; color: #0f172a; margin: 0; padding: 8px 12px 24px; }
    h1 { font-size: 18px; margin: 0 0 4px; }
    .meta { font-size: 12px; color: #64748b; margin: 0 0 14px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: 0.04em;
         color: #475569; border-bottom: 2px solid #cbd5e1; padding: 6px 8px; }
    td { border-bottom: 1px solid #e2e8f0; padding: 6px 8px; vertical-align: top; }
    tr:nth-child(even) td { background: #f8fafc; }
  </style>
</head>
<body>
  <h1>🔑 Listado de llaves</h1>
  <p class="meta">${escHtml(rows.length)} propiedades · ${fuera} fuera o pendientes · Impreso ${escHtml(ahora)}</p>
  <table>
    <thead>
      <tr>
        <th>Ref.</th><th>Dirección</th><th>Población</th><th>Nº llave</th>
        <th>Estado</th><th>Con quién</th><th>Desde</th><th>Teléfono</th>
      </tr>
    </thead>
    <tbody>${bodyRows}</tbody>
  </table>
</body>
</html>`;

  // iframe oculto — solo el diálogo de impresión, sin pestaña nueva
  const FRAME_ID = "__vc_print_llaves_iframe";
  let iframe = document.getElementById(FRAME_ID);
  if (iframe) iframe.remove();
  iframe = document.createElement("iframe");
  iframe.id = FRAME_ID;
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;left:-9999px;top:-9999px;width:297mm;height:210mm;border:none;opacity:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(iframe);

  const cleanup = () => {
    setTimeout(() => {
      try {
        iframe.remove();
      } catch {
        /* noop */
      }
    }, 1000);
  };

  const frameDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!frameDoc) {
    cleanup();
    return;
  }
  frameDoc.open("text/html", "replace");
  frameDoc.write(html);
  frameDoc.close();

  const win = iframe.contentWindow;
  if (!win) {
    cleanup();
    return;
  }
  const lanzar = () => {
    try {
      win.focus();
      win.print();
    } catch {
      cleanup();
    }
  };
  win.addEventListener("afterprint", cleanup, { once: true });
  setTimeout(cleanup, 300_000);
  if (frameDoc.readyState === "complete") {
    setTimeout(lanzar, 150);
  } else {
    win.addEventListener("load", () => setTimeout(lanzar, 150), { once: true });
  }
}

export default function LlavesFuera() {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortStack, setSortStack] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/propiedades/llaves-fuera");
      setRows(Array.isArray(data?.data) ? data.data : []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Error al cargar");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const rowsOrdenadas = useMemo(() => sortRowsByStack(rows, sortStack), [rows, sortStack]);

  const ordenActivoTxt = useMemo(() => {
    if (!sortStack.length) return "";
    return sortStack
      .map((s, i) => `${i + 1}. ${SORT_LABEL[s.key] || s.key} ${s.dir === "asc" ? "↑" : "↓"}`)
      .join(" · ");
  }, [sortStack]);

  const fuera = rows.filter((r) =>
    ["pendiente_aceptacion", "externo"].includes(String(r.llaves_ubicacion || "")),
  ).length;

  return (
      <div style={{ padding: "20px 24px", maxWidth: 1280, margin: "0 auto", fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: c.textPrimary }}>🔑 Llaves</h1>
            <p style={{ margin: "6px 0 0", fontSize: 13, color: c.textMuted }}>
              Propiedades con llaves (sin vendidas, alquiladas ni eliminadas).
              {!loading && rows.length > 0
                ? ` ${rows.length} en total${fuera ? ` · ${fuera} fuera o pendientes` : ""}.`
                : ""}
            </p>
            {ordenActivoTxt ? (
              <p style={{ margin: "4px 0 0", fontSize: 12, color: c.textSecondary }}>
                Orden: {ordenActivoTxt}
                {sortStack.length > 1 ? (
                  <span style={{ color: c.textMuted }}>
                    {" "}— el 2.º solo desempata (mismo nº de llave)
                  </span>
                ) : null}
              </p>
            ) : null}
          </div>
          {!loading && rowsOrdenadas.length > 0 && (
            <button
              type="button"
              onClick={() => imprimirListadoLlaves(rowsOrdenadas)}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: `1px solid ${c.border}`,
                background: isDark ? c.bgCard : "#fff",
                color: c.textPrimary,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 13,
                whiteSpace: "nowrap",
              }}
            >
              🖨️ Imprimir listado
            </button>
          )}
        </div>

        {loading && <p style={{ color: c.textMuted }}>Cargando…</p>}
        {error && <p style={{ color: "#dc2626" }}>{error}</p>}
        {!loading && !error && rows.length === 0 && (
          <p style={{ color: c.textMuted }}>Ninguna propiedad con «tenemos llaves».</p>
        )}

        {!loading && rows.length > 0 && (
          <div
            style={{
              borderRadius: 12,
              border: `1px solid ${c.border}`,
              overflow: "hidden",
              background: isDark ? c.bgCard : "#fff",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: isDark ? "rgba(255,255,255,0.04)" : "#f8fafc" }}>
                  {HEADER_COLS.map((col, i) => {
                    const sortEntry = col.key ? sortStack.find((s) => s.key === col.key) : null;
                    const isActive = !!sortEntry;
                    return (
                      <th
                        key={col.label || `acciones-${i}`}
                        role={col.key ? "button" : undefined}
                        tabIndex={col.key ? 0 : undefined}
                        onClick={col.key ? () => setSortStack((prev) => cycleSortStack(prev, col.key)) : undefined}
                        onKeyDown={col.key ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSortStack((prev) => cycleSortStack(prev, col.key));
                          }
                        } : undefined}
                        onMouseEnter={col.key ? (e) => {
                          const arrow = e.currentTarget.querySelector("[data-sort-arrow]");
                          if (arrow && !isActive) arrow.style.opacity = "1";
                        } : undefined}
                        onMouseLeave={col.key ? (e) => {
                          const arrow = e.currentTarget.querySelector("[data-sort-arrow]");
                          if (arrow && !isActive) arrow.style.opacity = "0";
                        } : undefined}
                        style={{
                          textAlign: "left",
                          padding: "10px 12px",
                          fontSize: 10,
                          fontWeight: 800,
                          letterSpacing: "0.06em",
                          textTransform: "uppercase",
                          color: c.textMuted,
                          borderBottom: `1px solid ${c.border}`,
                          cursor: col.key ? "pointer" : "default",
                          userSelect: "none",
                        }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          {col.label}
                          {col.key ? (
                            <span
                              data-sort-arrow=""
                              style={{
                                opacity: isActive ? 1 : 0,
                                transition: "opacity 0.15s",
                                fontSize: 10,
                                color: "#fff",
                                textShadow: "0 0 2px rgba(0,0,0,0.75), 0 1px 2px rgba(0,0,0,0.55)",
                                lineHeight: 1,
                              }}
                            >
                              {isActive ? (
                                <>
                                  {sortEntry.dir === "asc" ? "↑" : "↓"}
                                  {sortStack.length > 1 ? (
                                    <span style={{ fontSize: 8, marginLeft: 1, opacity: 0.9 }}>
                                      {sortStack.findIndex((s) => s.key === col.key) + 1}
                                    </span>
                                  ) : null}
                                </>
                              ) : "↕"}
                            </span>
                          ) : null}
                        </span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {rowsOrdenadas.map((r) => {
                  const est = etiquetaUbicacion(r.llaves_ubicacion);
                  return (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${c.border}` }}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: c.textPrimary, whiteSpace: "nowrap" }}>{r.referencia}</td>
                    <td style={{ padding: "10px 12px", color: c.textPrimary, maxWidth: 260 }}>{r.direccion || "—"}</td>
                    <td style={{ padding: "10px 12px", color: c.textSecondary, whiteSpace: "nowrap" }}>{r.poblacion || "—"}</td>
                    <td style={{ padding: "10px 12px", color: c.textSecondary }}>{r.num_llave || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <span
                        style={{
                          padding: "2px 8px",
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 700,
                          background: est.bg,
                          color: est.color,
                        }}
                      >
                        {est.label}
                      </span>
                    </td>
                    <td style={{ padding: "10px 12px", color: c.textPrimary }}>
                      {r.llaves_con_quien || [r.agencia, r.persona].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td style={{ padding: "10px 12px", color: c.textSecondary }}>{fmtWhen(r.llaves_desde)}</td>
                    <td style={{ padding: "10px 12px", color: c.textSecondary }}>{r.llaves_telefono || "—"}</td>
                    <td style={{ padding: "10px 12px" }}>
                      <button
                        type="button"
                        onClick={() => navigate(`/propiedades/${r.id}/editar`, { state: { focusLlaves: true } })}
                        style={{
                          padding: "5px 10px",
                          borderRadius: 6,
                          border: `1px solid ${c.border}`,
                          background: "transparent",
                          color: c.textPrimary,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          fontSize: 12,
                        }}
                      >
                        Abrir
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
  );
}
